import React, { useState, useEffect } from 'react';
import { IonIcon } from '@ionic/react';
import { alertCircle } from 'ionicons/icons';
import Credits from '../../services/credits';
import '../../pages/MarketplacePage/MarketplaceDetailModal.css';
import './AddCreditsModal.css';

// Defaults used until the live /credits/limits response lands. Backend is
// the source of truth — these are just a paint-render fallback so the
// modal opens instantly with sensible numbers.
const DEFAULT_QUICK_AMOUNTS = [25, 50, 100, 250];
const DEFAULT_MIN = 25;
const DEFAULT_MAX = 10000;

/**
 * AddCreditsModal — Stripe Checkout entry point for topping up credits.
 *
 * Visual shell reuses the marketplace deploy modal classes
 * (`mkt-modal-*`) so this looks like a sibling of the workflow / app
 * deploy modals. The Continue button uses `.mkt-modal-deploy` for the
 * exact same green-pill appearance as Deploy.
 *
 * Limits (min / max / quick-pick amounts) are fetched from
 * GET /credits/limits on every open — so admin edits in
 * backend/src/config/pricing-rates.js (or per-account override via
 * update-account-rates.js) are picked up without a frontend rebuild.
 *
 * Flow:
 *   1. Modal opens → fetch /credits/limits → render bounds
 *   2. User picks a quick-amount button OR types a custom value
 *   3. Client validates against live bounds (backend re-validates anyway)
 *   4. POST /credits/checkout → Stripe URL
 *   5. window.location.href = url   (full redirect, NOT new tab — Stripe's
 *      success URL is /billing/credits?checkout=success&session_id=X
 *      and the page rehydrates the balance with a toast)
 */
const AddCreditsModal = ({ isOpen, onClose }) => {
  const [limits, setLimits] = useState({
    minTopupUsd: DEFAULT_MIN,
    maxTopupUsd: DEFAULT_MAX,
    quickAmountsUsd: DEFAULT_QUICK_AMOUNTS,
  });
  const [selected, setSelected] = useState(DEFAULT_QUICK_AMOUNTS[0]);
  const [customMode, setCustomMode] = useState(false);
  const [customValue, setCustomValue] = useState('');
  const [error, setError] = useState('');
  const [submitting, setSubmitting] = useState(false);

  // Reset every time the modal opens so subsequent opens don't show
  // stale state from a previous attempt. ALSO fetch the live limits
  // (backend may have an account-specific override).
  useEffect(() => {
    if (!isOpen) return;
    let cancelled = false;
    setCustomMode(false);
    setCustomValue('');
    setError('');
    setSubmitting(false);
    Credits.getLimits()
      .then((next) => {
        if (cancelled) return;
        setLimits(next);
        setSelected(next.quickAmountsUsd[0] || next.minTopupUsd);
      })
      .catch(() => { /* getLimits already falls back internally */ });
    return () => { cancelled = true; };
  }, [isOpen]);

  if (!isOpen) return null;

  const QUICK_AMOUNTS = limits.quickAmountsUsd;
  const MIN_AMOUNT = limits.minTopupUsd;
  const MAX_AMOUNT = limits.maxTopupUsd;

  const effectiveAmount = customMode
    ? Number(customValue)
    : selected;
  const isValid = Number.isFinite(effectiveAmount) &&
    effectiveAmount >= MIN_AMOUNT &&
    effectiveAmount <= MAX_AMOUNT;

  const pickQuick = (amount) => {
    setSelected(amount);
    setCustomMode(false);
    setError('');
  };

  const handleContinue = async () => {
    if (!isValid || submitting) return;
    setError('');
    setSubmitting(true);
    try {
      const res = await Credits.createCheckout(effectiveAmount, limits);
      if (res?.url) {
        window.location.href = res.url;
        return;
      }
      setError('Stripe did not return a checkout URL.');
      setSubmitting(false);
    } catch (err) {
      setError(err?.message || 'Could not start checkout. Try again.');
      setSubmitting(false);
    }
  };

  const handleOverlayClick = () => {
    if (submitting) return;
    onClose();
  };

  return (
    <div className="mkt-modal-overlay" onClick={handleOverlayClick} role="presentation">
      <div
        className="mkt-modal add-credits-modal"
        role="dialog"
        aria-label="Add credits"
        onClick={(e) => e.stopPropagation()}
      >
        <button
          type="button"
          className="mkt-modal-close"
          onClick={onClose}
          aria-label="Close"
          disabled={submitting}
        >×</button>

        <div className="mkt-modal-body">
          <header className="mkt-modal-header add-credits-header">
            <h2 className="mkt-modal-title">Add Credits</h2>
            {/* Tagline removed — title alone is enough; rate context lives
                on the BillingPage so the modal stays focused on the amount
                pick. */}
          </header>

          {/* Quick-pick buttons */}
          <div className="add-credits-quick-row" role="radiogroup" aria-label="Quick amount">
            {QUICK_AMOUNTS.map((amount) => {
              const isActive = !customMode && selected === amount;
              return (
                <button
                  key={amount}
                  type="button"
                  role="radio"
                  aria-checked={isActive}
                  className={`add-credits-quick ${isActive ? 'is-active' : ''}`}
                  onClick={() => pickQuick(amount)}
                  disabled={submitting}
                >
                  ${amount}
                </button>
              );
            })}
            <button
              type="button"
              role="radio"
              aria-checked={customMode}
              className={`add-credits-quick add-credits-quick-custom ${customMode ? 'is-active' : ''}`}
              onClick={() => { setCustomMode(true); setError(''); }}
              disabled={submitting}
            >
              Custom
            </button>
          </div>

          {customMode && (
            <div className="add-credits-custom-row">
              <label htmlFor="add-credits-custom" className="add-credits-custom-label">
                Custom amount (USD)
              </label>
              <div className="add-credits-custom-input-wrap">
                <span className="add-credits-custom-prefix">$</span>
                <input
                  id="add-credits-custom"
                  type="number"
                  min={MIN_AMOUNT}
                  max={MAX_AMOUNT}
                  step="1"
                  className="add-credits-custom-input"
                  value={customValue}
                  onChange={(e) => { setCustomValue(e.target.value); setError(''); }}
                  placeholder={`${MIN_AMOUNT} – ${MAX_AMOUNT}`}
                  disabled={submitting}
                  autoFocus
                />
              </div>
              {customValue !== '' && !isValid && (
                <p className="add-credits-custom-hint">
                  Enter a whole-dollar amount between ${MIN_AMOUNT} and ${MAX_AMOUNT}.
                </p>
              )}
            </div>
          )}

          {/* Pricing-context list removed — duplicated the rate card the
              user already sees on /billing. Modal is a pure "pick amount
              → checkout" flow. */}

          {error && (
            <div className="add-credits-error" role="alert">
              <IonIcon icon={alertCircle} aria-hidden="true" /> {error}
            </div>
          )}
        </div>

        <footer className="mkt-modal-footer add-credits-footer">
          <button
            type="button"
            className="add-credits-cancel"
            onClick={onClose}
            disabled={submitting}
          >
            Cancel
          </button>
          <span className="mkt-modal-deploy-split">
            <button
              type="button"
              className="mkt-modal-deploy"
              disabled={!isValid || submitting}
              onClick={handleContinue}
            >
              {submitting ? 'Redirecting…' : `Continue — $${effectiveAmount || 0}`}
            </button>
          </span>
        </footer>
      </div>
    </div>
  );
};

export default AddCreditsModal;
